import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDisclosure } from "@mantine/hooks";
import { t } from "i18next";
import { CSSProperties, createContext, useContext, useMemo, useState } from "react";

import { colors } from "#app/theme/v1/colors";
import { fontSizes } from "#app/theme/v1/fontSizes";
import { spacing } from "#app/theme/v1/spacing";
import { useCompany } from "#modules/company/view/useCompany";
import { CreateDefaultProcessStep } from "#modules/defaultProcessSteps/infra/createDefaultProcessStep.api";
import { useDeleteDefaultProcessStep } from "#modules/defaultProcessSteps/view/useDeleteDefaultProcessStep";
import { type DefaultProcessStepSection } from "#modules/onboardingV2/domain/splitDefaultProcessSteps";
import { isPlainNumberString } from "#shared/helpers/isPlainNumberString";
import { Icon } from "#shared/icons/Icon";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Group } from "#shared/view/components/Group";
import { Paper } from "#shared/view/components/Paper";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Table } from "#shared/view/designSystem/Table";
import { formatPrice } from "shared";

import {
  canHaveAssemblies,
  DefaultProcessStepFormModal,
  getPartLevelsPreset,
} from "./DefaultProcessStepFormModal";

const formatTimeCell = (value: string | null) =>
  value !== null && isPlainNumberString(value) ? `${value} min` : value;

type DragHandleContextValue = Pick<ReturnType<typeof useSortable>, "attributes" | "listeners">;

const DragHandleContext = createContext<DragHandleContextValue | null>(null);

const RowDragHandleCell = () => {
  const dragHandle = useContext(DragHandleContext);
  if (!dragHandle) {
    return null;
  }

  return (
    <span
      {...dragHandle.attributes}
      {...dragHandle.listeners}
      style={{ cursor: "grab", display: "flex", alignItems: "center" }}
    >
      <Icon name="gripVertical" size={12} color={colors.gray[5]} />
    </span>
  );
};

type DraggableRowProps = {
  rowId: string;
  children: React.ReactNode;
};

const DraggableRow = ({ rowId, children }: DraggableRowProps) => {
  const { attributes, listeners, transform, transition, setNodeRef, isDragging } = useSortable({
    id: rowId,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1 : 0,
    position: "relative",
  };

  return (
    <DragHandleContext.Provider value={{ attributes, listeners }}>
      <Table.Tr ref={setNodeRef} style={style}>
        {children}
      </Table.Tr>
    </DragHandleContext.Provider>
  );
};

export type DefaultProcessStepForTable = CreateDefaultProcessStep & {
  id: string;
};

type DefaultProcessStepsTableProps = {
  companyId: string;
  section: DefaultProcessStepSection;
  processSteps: DefaultProcessStepForTable[];
  onProcessStepUpdated?: (updated: DefaultProcessStepForTable) => void;
  onProcessStepDeleted?: (index: number) => void;
};

export const DefaultProcessStepsTable = ({
  companyId,
  section,
  processSteps,
  onProcessStepUpdated,
  onProcessStepDeleted,
}: DefaultProcessStepsTableProps) => {
  const { company } = useCompany();

  const displayPartLevels = canHaveAssemblies(company);

  const hasProcessSteps = processSteps.length > 0;

  const { deleteDefaultProcessStep } = useDeleteDefaultProcessStep();

  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [selectedProcessStep, setSelectedProcessStep] = useState<DefaultProcessStepForTable | null>(
    null,
  );

  const onEditRow = (ps: DefaultProcessStepForTable) => {
    setSelectedProcessStep(ps);
    openEditModal();
  };

  const onRemoveRow = (ps: DefaultProcessStepForTable, index: number) => {
    deleteDefaultProcessStep({ id: ps.id });
    onProcessStepDeleted?.(index);
  };

  const dataIds = useMemo(() => processSteps.map((ps) => ps.id), [processSteps]);
  const { setNodeRef, isOver } = useDroppable({ id: section });

  const tableContent = (
    <Stack
      ref={setNodeRef}
      gap="xxs"
      mih={40}
      style={{
        borderRadius: 8,
        outline: isOver ? `1px dashed ${colors.blue[4]}` : undefined,
        outlineOffset: 2,
      }}
    >
      {hasProcessSteps && (
        <Paper radius={"sm"} bg={colors.gray[1]}>
          <Table
            layout="fixed"
            w="100%"
            styles={{
              td: { padding: spacing.xxxs, fontSize: fontSizes.tiny },
              th: { padding: spacing.xxxs, fontSize: fontSizes.tiny },
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 30 }} />
                <Table.Th style={{ width: "15%" }}>{t("quoteRequestLine.processSteps")}</Table.Th>
                <Table.Th style={{ width: "25%" }}>{t("processStep.description")}</Table.Th>
                <Table.Th style={{ width: "15%" }}>
                  {t("processStep.machinePreparationTime")}
                </Table.Th>
                <Table.Th style={{ width: "15%" }}>{t("processStep.otherMachineTime")}</Table.Th>
                <Table.Th style={{ width: "10%" }}>{t("processStep.machineHourlyPrice")}</Table.Th>
                {displayPartLevels && (
                  <Table.Th style={{ width: "15%" }}>{t("processStep.partLevels")}</Table.Th>
                )}
                <Table.Th style={{ width: "5%" }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <SortableContext id={section} items={dataIds} strategy={verticalListSortingStrategy}>
                {processSteps.map((ps, index) => (
                  <DraggableRow key={ps.id} rowId={ps.id}>
                    <Table.Td>
                      <RowDragHandleCell />
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{t(`industrialProcesses.${ps.industrialProcess}`)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{ps.description ?? "-"}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {ps.preparationTimeFormula !== null
                          ? formatTimeCell(ps.preparationTimeFormula)
                          : t("settings.defaultProcessStepTimeMode.model")}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {ps.cycleTimeFormula !== null
                          ? formatTimeCell(ps.cycleTimeFormula)
                          : t("settings.defaultProcessStepTimeMode.model")}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{formatPrice(ps.machineHourlyPrice, "€/h")}</Text>
                    </Table.Td>
                    {displayPartLevels && (
                      <Table.Td>
                        <Text size="xs">
                          {t(
                            `processStep.partLevelsOptions.${getPartLevelsPreset(ps.partLevels, companyId)}`,
                          )}
                        </Text>
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Group gap="xxs">
                        <ActionIcon variant="subtle" size="sm" onClick={() => onEditRow(ps)}>
                          <Icon name="edit" size={14} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => onRemoveRow(ps, index)}
                        >
                          <Icon name="trash" size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </DraggableRow>
                ))}
              </SortableContext>
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {!hasProcessSteps && <Text c={colors.gray[4]}>Aucun procédé par défaut</Text>}
    </Stack>
  );

  return (
    <>
      {tableContent}

      {selectedProcessStep && (
        <DefaultProcessStepFormModal
          companyId={companyId}
          opened={editModalOpened}
          onClose={() => {
            closeEditModal();
            setSelectedProcessStep(null);
          }}
          mode="edit"
          initialData={selectedProcessStep}
          onSuccess={(updated) => {
            closeEditModal();
            setSelectedProcessStep(null);
            onProcessStepUpdated?.(updated);
          }}
        />
      )}
    </>
  );
};
